import Footer from '@react/shared/components/Footer';
import Image from '@react/shared/components/Image';
import { AuthorBody } from '@react/shared/types/usePrefetch';
import { RESOURCE_CENTER_PATH } from '@sly/core/constants/dashboardAppPaths';
import { populate } from '@sly/core/constants/populate';
import cx from 'classnames';
import React, { useEffect } from 'react';
import { assetPath } from 'sly/lib/path';
import Header from 'sly/resourceCenter/components/Header';
import Helmet from 'sly/resourceCenter/components/Helmet';
import NavPaths from 'sly/resourceCenter/components/NavPaths';
import ResourceCenterChatBotWrapper from 'sly/resourceCenter/components/ResourceCenterChatBotWrapper';
import AuthorCard from 'sly/resourceCenter/pages/AuthorIndex/authorCard';
import { usePrefetch } from 'sly/services/api/prefetch';
import { pageNames, useEvents } from 'sly/services/events';

import { pagePadding } from '../../styles';

interface GroupedAuthors {
  [key: string]: AuthorBody[];
}

const groupBy = function (xs: AuthorBody[]) {
  return xs.reduce((rv: GroupedAuthors, x) => {
    (rv[x.category] = rv[x.category] || []).push(x);
    return rv;
  }, {});
};

const AuthorIndexPage = () => {
  const {
    requestInfo: { result, hasFinished },
  } = usePrefetch('getAuthor', {
    populate: populate.authors,
    'filters[status]': 'active',
    sort: 'order',
  });
  const authors = result?.data || [];

  const { events } = useEvents();
  useEffect(() => {
    events.page(pageNames.Authors, {});
  }, []);

  if (!hasFinished) {
    return (
      <div
        className='flex h-[100vh] items-center justify-center'
        data-tp-id='gen-div-8e8d1543-12a1-4b8d-92c8-f30a8fa49c46'
      >
        <Image
          src={assetPath('uploads/react-assets/homebase/loader.svg')}
          layout='fill'
          alt='loader'
          data-tp-id='gen-Image-e6163b37-e64f-41e9-8bec-88371836f0b0'
        />
      </div>
    );
  }

  const paths = [
    {
      name: 'Resource Center Home',
      to: RESOURCE_CENTER_PATH,
    },
    {
      name: 'Authors',
      to: null,
    },
  ];

  const groupedAuthors: GroupedAuthors | Record<string, never> = authors
    ? groupBy(authors)
    : {};
  const categories = ['seniorly'];
  categories.push(
    ...Object.keys(groupedAuthors).filter((key) => key !== 'seniorly')
  );

  return (
    <>
      <ResourceCenterChatBotWrapper>
        <Helmet
          path={`${RESOURCE_CENTER_PATH}/authors`}
          title='Authors | Seniorly'
          shortDescription="Meet the talented authors behind Seniorly's informative senior living content. Explore their expertise and passion for providing valuable insights and resources for seniors and their families"
        />

        <Header />

        <div
          className='bg-viridian-lighter-90'
          data-tp-id='gen-div-dfcbf71d-24be-4ad6-a443-e74deecc91dc'
        >
          <div
            className='mx-6 py-xxl px-0 md:py-xxxl lg:mx-[240px]'
            data-tp-id='gen-div-7519230b-f63f-4aef-af05-f5843ca00d46'
          >
            <div className='md:mx-auto'>
              <NavPaths paths={paths} />
              <h1
                className='font-t-xl pt-m'
                data-tp-id='gen-div-296ba96f-4938-42b7-9695-0c501dadf384'
              >
                Meet our authors
              </h1>
              <div
                className='font-b-m my-6 mx-0 max-w-[680px]'
                data-tp-id='gen-div-f15ab12e-847b-44ef-8e8f-7c5c4911a451'
              >
                Seniorly’s team of experienced writers and editors is
                responsible for creating the high-quality content that its
                readers enjoy. The team is made up of on-staff content managers
                as well as freelancers who work together with knowledgeable
                contributors to ensure that the content that is created advances
                Seniorly’s mission of helping as many seniors and their
                caregivers as possible through empathetic, professional
                guidance.
              </div>
            </div>
          </div>
        </div>
        <hr />

        <div
          className={cx(pagePadding, 'flex flex-col justify-center')}
          data-tp-id='gen-div-1a2208cb-4853-488c-af40-bf78f8a723e1'
        >
          <>
            {categories &&
              categories.map((category) => (
                <>
                  {category !== 'seniorly' &&
                    category !== 'undefined' &&
                    category !== 'null' && (
                      <>
                        <hr />
                        <div
                          className='font-t-l my-xxl mx-0 text-center capitalize md:my-xxxl'
                          data-tp-id='gen-div-e9d4efb2-4737-46d8-9f64-71eb1eef929e'
                        >
                          {category} contributors
                        </div>
                        <hr />
                      </>
                    )}
                  {groupedAuthors[category] &&
                    groupedAuthors[category].map((author, index) => (
                      <>
                        <div
                          className='my-xxl mx-0 md:my-xxxl lg:mx-[216px]'
                          data-tp-id='gen-div-476844f7-763c-4f04-b6f6-7a751bdfbee0'
                        >
                          <AuthorCard author={author} />
                        </div>
                        {index < groupedAuthors[category].length - 1 ? (
                          <hr />
                        ) : (
                          ''
                        )}
                      </>
                    ))}
                </>
              ))}
          </>
        </div>
        <Footer />
      </ResourceCenterChatBotWrapper>
    </>
  );
};

AuthorIndexPage.displayName = 'AuthorIndex';

export default AuthorIndexPage;
